import { Client } from "@xmpp/client"
import { useEffect, useState } from "react"
import AdHocCommands from "@/api/adHocCommands"
import { extractVhostFromJid } from "@/jidUtils"
import { FuelStation } from "@/lib/fuelStations"
import FuelStationsParser from "@/lib/fuelStationsParser"

const fuelStationsCache = new Map<string, FuelStation[]>()
const fuelStationsRequests = new Map<string, Promise<FuelStation[]>>()

const getFuelStationsForClient = async (
  client: Client,
): Promise<FuelStation[]> => {
  const vhost = extractVhostFromJid(client.jid!.toString())
  const cachedStations = fuelStationsCache.get(vhost)
  if (cachedStations) {
    return cachedStations
  }

  const pendingRequest = fuelStationsRequests.get(vhost)
  if (pendingRequest) {
    return pendingRequest
  }

  const request = AdHocCommands.getFuelStations(client)
    .then((response) => {
      const stations = FuelStationsParser.parse(response)?.stations ?? []
      fuelStationsCache.set(vhost, stations)
      return stations
    })
    .catch((error) => {
      fuelStationsRequests.delete(vhost)
      throw error
    })

  fuelStationsRequests.set(vhost, request)
  return request
}

export const useFuelStations = (enabled: boolean): FuelStation[] => {
  const [stations, setStations] = useState<FuelStation[]>(() => {
    if (!enabled || !window.XMPP?.jid) {
      return []
    }

    return (
      fuelStationsCache.get(extractVhostFromJid(window.XMPP.jid.toString())) ??
      []
    )
  })

  useEffect(() => {
    if (!enabled || !window.XMPP?.jid) {
      return
    }

    let isCancelled = false

    void getFuelStationsForClient(window.XMPP)
      .then((nextStations) => {
        if (!isCancelled) {
          setStations(nextStations)
        }
      })
      .catch((error) => {
        console.error("Failed to fetch fuel stations:", error)
      })

    return () => {
      isCancelled = true
    }
  }, [enabled])

  return stations
}
